import React, { useEffect, useRef, useState } from "react";
import { calculateDistance } from "../utils/calculateDistance";

// TC-10 — how long an operator has to respond before the job is auto-declined
// and handed to the next suitable vehicle.
export const RESPONSE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

export default function BookingAlertOverlay({
  booking,
  isVisible,
  onAccept,
  onCancel,
  onTimeout,
  isProcessing,
  timeoutMs = RESPONSE_TIMEOUT_MS,
}) {
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const timedOutRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(timeoutMs / 1000));
  const fromValue = booking?.From || booking?.fromLocation || booking?.WorkArea || "";
  const toValue = booking?.TO || booking?.toLocation || "";
  const workScopeValue = booking?.WorkScope || booking?.workScope || "";
  const scheduledTimeValue = booking?.bsTime || booking?.bookedDate || "";

  const startLat = booking?.slatitude;
  const startLong = booking?.slongitude;
  const endLat = booking?.elatitude;
  const endLong = booking?.elongitude;

  // Play sound immediately when overlay becomes visible
  useEffect(() => {
    if (isVisible && booking) {
      console.log("BookingAlertOverlay: Playing sound immediately");
      
      // Stop any existing audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Use Web Audio API for immediate, reliable sound
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        const playRingTone = () => {
          const now = audioContext.currentTime;
          
          // Uber-style attention chime - pleasant but urgent
          // First chime (ding)
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.connect(gain1);
          gain1.connect(audioContext.destination);
          osc1.frequency.value = 880; // A5
          osc1.type = "sine";
          gain1.gain.setValueAtTime(0.7, now);
          gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          osc1.start(now);
          osc1.stop(now + 0.3);

          // Second chime (higher ding)
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 1318.51; // E6
          osc2.type = "sine";
          gain2.gain.setValueAtTime(0.7, now + 0.15);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          osc2.start(now + 0.15);
          osc2.stop(now + 0.5);

          // Third sustained tone (attention grabber)
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          osc3.connect(gain3);
          gain3.connect(audioContext.destination);
          osc3.frequency.value = 1760; // A6
          osc3.type = "sine";
          gain3.gain.setValueAtTime(0.6, now + 0.35);
          gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          osc3.start(now + 0.35);
          osc3.stop(now + 0.8);

          // Soft low undertone for richness
          const osc4 = audioContext.createOscillator();
          const gain4 = audioContext.createGain();
          osc4.connect(gain4);
          gain4.connect(audioContext.destination);
          osc4.frequency.value = 440; // A4
          osc4.type = "triangle";
          gain4.gain.setValueAtTime(0.3, now);
          gain4.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          osc4.start(now);
          osc4.stop(now + 0.6);
        };

        // Play immediately
        playRingTone();
        
        // Repeat every 2 seconds
        intervalRef.current = setInterval(playRingTone, 2000);
        
        // Store reference for cleanup
        audioRef.current = { 
          context: audioContext,
          pause: () => {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        };

        console.log("BookingAlertOverlay: Ring tone started");

      } catch (e) {
        console.error("Web Audio API failed:", e);
      }

      // Vibrate on mobile
      if (navigator.vibrate) {
        navigator.vibrate([400, 200, 400, 200, 400]);
      }
    }

    // Cleanup when overlay closes or component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isVisible, booking]);

  // ── TC-10: response countdown ──────────────────────────────────────────────
  // While the alert is up, tick down from `timeoutMs`. On expiry the job is
  // auto-declined so it can be reassigned to the next suitable vehicle.
  // NOTE: keyed on booking.bId, NOT the booking object. The same booking is
  // delivered by several channels (SignalR + two bookingWS listeners), each
  // creating a fresh object — depending on identity would restart the
  // countdown on every duplicate delivery and it would never expire.
  const bookingId = booking?.bId;

  useEffect(() => {
    if (!isVisible || !bookingId) return undefined;

    timedOutRef.current = false;
    const deadline = Date.now() + timeoutMs;
    setSecondsLeft(Math.ceil(timeoutMs / 1000));

    countdownRef.current = setInterval(() => {
      const remainingMs = deadline - Date.now();
      setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));

      if (remainingMs <= 0 && !timedOutRef.current) {
        timedOutRef.current = true;
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        console.log("BookingAlertOverlay: No response - auto-declining for reassignment");
        stopAlertSound();
        // fall back to onCancel if no dedicated timeout handler was supplied
        (onTimeout || onCancel)?.();
      }
    }, 250);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, bookingId, timeoutMs]);

  const stopAlertSound = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const stopCountdown = () => {
    timedOutRef.current = true; // prevent a late tick firing after a manual choice
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // Stop sound and accept
  const handleAccept = () => {
    console.log("BookingAlertOverlay: Accept clicked - stopping sound");
    stopCountdown();
    stopAlertSound();
    onAccept();
  };

  // Stop sound and cancel
  const handleCancel = () => {
    console.log("BookingAlertOverlay: Cancel clicked - stopping sound");
    stopCountdown();
    stopAlertSound();
    onCancel();
  };

  if (!isVisible || !booking) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center px-3 sm:px-4" style={{ zIndex: 9999 }}>
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        style={{ zIndex: 9999 }}
      />
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center animate-pulse" style={{ zIndex: 10000 }}>
        <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🚗</div>
        <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-3 sm:mb-4">New Trip Request!</h2>

        {/* TC-10 — response deadline */}
        <div className="mb-4 sm:mb-6">
          <div
            className={`text-2xl sm:text-3xl font-bold tabular-nums ${
              secondsLeft <= 30 ? "text-red-600" : "text-blue-700"
            }`}
          >
            {String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:
            {String(secondsLeft % 60).padStart(2, "0")}
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Respond before time runs out or this trip goes to another vehicle
          </p>
          <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                secondsLeft <= 30 ? "bg-red-500" : "bg-blue-500"
              }`}
              style={{ width: `${Math.max(0, (secondsLeft * 1000 * 100) / timeoutMs)}%` }}
            />
          </div>
        </div>
        <div className="space-y-2 sm:space-y-3 text-base sm:text-lg">
          <p><strong>Booking ID:</strong> #{booking.bId}</p>
          <p><strong>Employee:</strong> {booking.employeeName || "Employee"}</p>
          <p style={{ color: booking.SubBook ? "#219230" : "#DC2626",fontWeight: "bold" }}><strong>Resources:</strong> {booking.SubBook || "NO"} </p>
          {/* <p><strong>Distance:</strong> {booking.totalDestence ? `${booking.totalDestence} km` : "Calculating..."}</p> */}
          <p><strong>Distance:</strong> {calculateDistance(startLat, startLong, endLat, endLong)} km</p>
          {scheduledTimeValue && (
            <p><strong>Scheduled:</strong> {scheduledTimeValue}</p>
          )}
          {(fromValue || toValue) && (
            <p><strong>Route:</strong> {fromValue || "N/A"} {toValue ? `-> ${toValue}` : ""}</p>
          )}
          {workScopeValue && (
            <p><strong>Work Scope:</strong> {workScopeValue}</p>
          )}
        </div>
        <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className={`flex-1 font-bold text-base sm:text-lg py-3 sm:py-4 rounded-lg sm:rounded-xl shadow-lg transition transform ${
              isProcessing 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-green-600 hover:bg-green-700 hover:scale-105"
            } text-white`}
          >
            {isProcessing ? "Processing..." : "✓ Accept"}
          </button>
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            className={`flex-1 font-bold text-base sm:text-lg py-3 sm:py-4 rounded-lg sm:rounded-xl shadow-lg transition transform ${
              isProcessing 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-red-600 hover:bg-red-700 hover:scale-105"
            } text-white`}
          >
            {isProcessing ? "Processing..." : "✗ Decline"}
          </button>
        </div>
      </div>
    </div>
  );
}
